import { CDN_URL_ICONS, CDN_URL_IMAGES, CDN_URL_VIDEOS } from '@/configs';
import { useIsInViewport } from '@/hooks/useIsInViewport';
import { isProduction } from '@/utils/commons';
import { useCallback, useEffect, useState } from 'react';
import { useRef } from 'react';
import { SectionControllers, StyledAbout } from './About.styled';
import s from './styles.module.scss';
import cs from 'classnames';
import useAnalyticsEventTracker, { AlphaActions } from '@/utils/ga';
import useWindowSize from '@/hooks/useWindowSize';
import { useRouter } from 'next/router';

import IOSIcon from './svg/IOSIcon';
import AndroidIcon from './svg/AndroidIcon';
import MobilePhoneIcon from './svg/MobilePhoneIcon';
import IcLogoA from '@/modules/AlphaWtf/svg/IcLogoA';
import IcDot from '@/modules/AlphaWtf/IcDot';

const APP_URL = '/app';

const AlphaWtfPage = () => {
  const gaEventTracker = useAnalyticsEventTracker();
  const { mobileScreen } = useWindowSize();
  const router = useRouter();
  const { referral } = router.query;
  const firstSectionRef = useRef<HTMLDivElement>(null);
  const secondSectionRef = useRef<HTMLDivElement>(null);
  const thirdSectionRef = useRef<HTMLDivElement>(null);
  const fourthSectionRef = useRef<HTMLDivElement>(null);
  const isInViewport1 = useIsInViewport(firstSectionRef);
  const isInViewport2 = useIsInViewport(secondSectionRef);
  const isInViewport3 = useIsInViewport(thirdSectionRef);
  const isInViewport4 = useIsInViewport(fourthSectionRef);

  const handleOnWheel = useCallback(
    (e: any) => {
      e.preventDefault();

      if (e.deltaY < 0) {
        setTimeout(() => {
          if (isInViewport2) {
            firstSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          } else if (isInViewport3) {
            secondSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          } else if (isInViewport4) {
            thirdSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        setTimeout(() => {
          if (isInViewport1) {
            secondSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          } else if (isInViewport2) {
            thirdSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          } else if (isInViewport3) {
            fourthSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    },
    [isInViewport1, isInViewport2, isInViewport3, isInViewport4]
  );

  const renderInstallButton = () => {
    return (
      <div className={s.app_install}>
        <div className={s.wInstall}>
          <a
            href={`${APP_URL}${referral ? `?referral=${referral}` : ''}`}
            onClick={() => gaEventTracker(AlphaActions.InstallAlphaApp)}
            className={s.install}
          >
            <MobilePhoneIcon />
            <p className={s.title}>Install the app</p>
          </a>
          <p className={s.text}>
            Available on both <IOSIcon /> <b>iOS</b> and <AndroidIcon />{' '}
            <b>Android</b>
          </p>
        </div>
      </div>
    );
  };

  return (
    <StyledAbout onWheel={handleOnWheel}>
      <div
        className={`section-01 ${s.section_01}`}
        id="section-1"
        ref={firstSectionRef}
      >
        <div className={s.sectionDrop}>
          <div className={s.sectionDrop_logo}>
            <IcLogoA />
          </div>
          <div className={s.sectionDrop_bottom}>
            <div className={s.sectionDrop_bottom_left}>
              <div className={s.sectionDrop_bottom_content}>
                Introducing the crypto super app.{' '}
                <b>Chat. Vibe. Trade. Play. All in one place.</b>
              </div>
            </div>
            <div className={s.sectionDrop_bottom_right}>
              <div className={s.sectionDrop_bottom_right_top}>
                <a
                  href={`${APP_URL}${referral ? `?referral=${referral}` : ''}`}
                  onClick={() => gaEventTracker(AlphaActions.InstallAlphaApp)}
                  className={s.install}
                >
                  Install the app
                </a>
              </div>
              <div className={s.note}>
                Available on both{' '}
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M8.60582 2.50156C9.17582 1.83763 9.97569 1.42153 10.8398 1.33943C10.8704 1.33833 10.9002 1.34894 10.9235 1.36916C10.9467 1.38938 10.9616 1.41773 10.9651 1.44856C11.0321 2.33947 10.7498 3.22119 10.1797 3.90225C9.92585 4.2272 9.60629 4.49323 9.24257 4.68241C8.87885 4.8716 8.47942 4.97955 8.07113 4.99903C8.01814 4.99903 7.96609 4.99677 7.91437 4.99323C7.8866 4.99098 7.86044 4.97914 7.84023 4.95969C7.82002 4.94024 7.80699 4.91437 7.80331 4.88636C7.77182 4.45451 7.82667 4.02065 7.96458 3.61084C8.10248 3.20103 8.3206 2.82373 8.60582 2.50156ZM3.44235 14.4497C2.05692 12.42 1.26075 9.07295 2.51323 6.86779C2.82631 6.3028 3.27805 5.82951 3.82459 5.49385C4.37113 5.15818 4.99391 4.97154 5.63252 4.95204H5.67123C6.19463 4.98408 6.70734 5.11625 7.18233 5.34157C7.45204 5.47397 7.74159 5.55991 8.03911 5.59588C8.32646 5.55662 8.60594 5.47177 8.86732 5.34446C9.41211 5.09016 9.999 4.94155 10.5977 4.90632C10.6799 4.90632 10.7605 4.90986 10.8385 4.91791C11.3828 4.93661 11.9153 5.08363 12.3938 5.34731C12.8723 5.61099 13.2837 5.98408 13.5955 6.43706C13.6045 6.45064 13.6108 6.46594 13.614 6.48203C13.6172 6.49812 13.6172 6.51469 13.614 6.53078C13.6108 6.54687 13.6045 6.56217 13.5955 6.57576C13.5864 6.58934 13.5747 6.60097 13.5612 6.60994L13.5491 6.61733C13.0946 6.90369 12.7184 7.30148 12.4553 7.77404C12.1921 8.2466 12.0503 8.77875 12.0431 9.32148C12.0452 9.93437 12.224 10.5333 12.5573 11.0444C12.8907 11.5555 13.3642 11.9566 13.9194 12.1982L13.928 12.2027C13.9554 12.2153 13.9772 12.2377 13.9893 12.2656C14.0013 12.2935 14.0028 12.325 13.9934 12.354L13.9889 12.3672C13.7501 13.1196 13.4066 13.8335 12.9687 14.4873C12.3849 15.3536 11.7185 16.3371 10.6396 16.3574C10.2301 16.357 9.82657 16.2586 9.46167 16.0702C9.07935 15.8768 8.65862 15.7742 8.2314 15.7702C7.7831 15.774 7.34139 15.8801 6.93894 16.0805C6.59774 16.2505 6.22697 16.3508 5.84767 16.3757H5.79309C4.83382 16.3757 4.1338 15.465 3.44235 14.4497Z"
                      fill="#F8F0AC"
                    />
                  </svg>{' '}
                  iOS
                </span>{' '}
                and{' '}
                <span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none"
                  >
                    <path
                      d="M9.9826 2.87076L10.6226 1.71609C10.6308 1.7013 10.636 1.68503 10.6379 1.66823C10.6398 1.65143 10.6384 1.63442 10.6337 1.61817C10.629 1.60192 10.6212 1.58675 10.6106 1.57352C10.6001 1.5603 10.5871 1.54928 10.5723 1.54109C10.5575 1.53291 10.5412 1.52771 10.5244 1.52581C10.5076 1.52391 10.4906 1.52534 10.4743 1.53001C10.4581 1.53469 10.4429 1.54252 10.4297 1.55305C10.4165 1.56359 10.4055 1.57663 10.3973 1.59143L9.7506 2.75809C9.19857 2.51606 8.60236 2.3911 7.9996 2.3911C7.39684 2.3911 6.80063 2.51606 6.2486 2.75809L5.60194 1.59143C5.5854 1.56137 5.55761 1.53911 5.52467 1.52954C5.49172 1.51998 5.45633 1.52389 5.42627 1.54043C5.39621 1.55696 5.37395 1.58475 5.36439 1.6177C5.35482 1.65064 5.35874 1.68603 5.37527 1.71609L6.01527 2.87076C5.40633 3.17109 4.89185 3.63329 4.52823 4.2067C4.16462 4.7801 3.96593 5.44256 3.95394 6.12142H12.0453C12.0332 5.44242 11.8343 4.77986 11.4704 4.20645C11.1065 3.63303 10.5918 3.1709 9.9826 2.87076ZM6.1326 4.64076C6.06572 4.64076 6.00035 4.62092 5.94475 4.58375C5.88915 4.54658 5.84583 4.49375 5.82027 4.43195C5.79471 4.37015 5.78805 4.30216 5.80115 4.23657C5.81424 4.17099 5.8465 4.11076 5.89384 4.06352C5.94117 4.01628 6.00146 3.98414 6.06707 3.97117C6.13268 3.95821 6.20066 3.96499 6.26241 3.99068C6.32416 4.01636 6.3769 4.05979 6.41396 4.11546C6.45102 4.17113 6.47073 4.23655 6.4706 4.30343C6.47043 4.39295 6.43474 4.47875 6.37137 4.542C6.308 4.60524 6.22213 4.64076 6.1326 4.64076ZM9.86727 4.64076C9.80039 4.64076 9.73502 4.62092 9.67942 4.58375C9.62382 4.54658 9.5805 4.49375 9.55493 4.43195C9.52937 4.37015 9.52272 4.30216 9.53581 4.23657C9.54891 4.17099 9.58117 4.11076 9.6285 4.06352C9.67584 4.01628 9.73613 3.98414 9.80174 3.97117C9.86735 3.95821 9.93533 3.96499 9.99708 3.99068C10.0588 4.01636 10.1116 4.05979 10.1486 4.11546C10.1857 4.17113 10.2054 4.23655 10.2053 4.30343C10.2051 4.39295 10.1694 4.47875 10.106 4.542C10.0427 4.60524 9.9568 4.64076 9.86727 4.64076ZM3.9526 12.3054C3.95243 12.4341 3.97766 12.5616 4.02686 12.6806C4.07605 12.7995 4.14824 12.9076 4.23929 12.9985C4.33033 13.0895 4.43844 13.1616 4.55742 13.2108C4.67639 13.2599 4.80389 13.285 4.9326 13.2848H5.58127V15.2848C5.58127 15.5253 5.67683 15.756 5.84692 15.9261C6.01702 16.0962 6.24772 16.1918 6.48827 16.1918C6.72882 16.1918 6.95952 16.0962 7.12962 15.9261C7.29971 15.756 7.39527 15.5253 7.39527 15.2848V13.2848H8.6046V15.2848C8.6046 15.5252 8.70013 15.7558 8.87016 15.9259C9.04019 16.0959 9.27081 16.1914 9.51127 16.1914C9.75173 16.1914 9.98235 16.0959 10.1524 15.9259C10.3224 15.7558 10.4179 15.5252 10.4179 15.2848V13.2848H11.0673C11.1958 13.2848 11.3231 13.2596 11.4419 13.2104C11.5607 13.1613 11.6686 13.0892 11.7595 12.9983C11.8504 12.9074 11.9225 12.7995 11.9716 12.6807C12.0208 12.5619 12.046 12.4346 12.0459 12.3061V6.44142H3.9526V12.3054ZM2.70794 6.28542C2.46747 6.28578 2.23696 6.38151 2.06698 6.55161C1.897 6.72171 1.80145 6.95229 1.80127 7.19276V10.9721C1.80127 11.0912 1.82472 11.2091 1.87029 11.3191C1.91585 11.4291 1.98263 11.529 2.06683 11.6132C2.15102 11.6974 2.25097 11.7642 2.36097 11.8097C2.47097 11.8553 2.58887 11.8788 2.70794 11.8788C2.827 11.8788 2.9449 11.8553 3.0549 11.8097C3.1649 11.7642 3.26485 11.6974 3.34905 11.6132C3.43324 11.529 3.50002 11.4291 3.54559 11.3191C3.59115 11.2091 3.6146 11.0912 3.6146 10.9721V7.19276C3.61425 6.9524 3.51861 6.72199 3.34866 6.55204C3.1787 6.38208 2.94829 6.28644 2.70794 6.28609V6.28542ZM13.2893 6.28542C13.0488 6.28578 12.8183 6.38151 12.6483 6.55161C12.4783 6.72171 12.3828 6.95229 12.3826 7.19276V10.9721C12.3826 11.0912 12.4061 11.2091 12.4516 11.3191C12.4972 11.4291 12.564 11.529 12.6482 11.6132C12.7324 11.6974 12.8323 11.7642 12.9423 11.8097C13.0523 11.8553 13.1702 11.8788 13.2893 11.8788C13.4083 11.8788 13.5262 11.8553 13.6362 11.8097C13.7462 11.7642 13.8462 11.6974 13.9304 11.6132C14.0146 11.529 14.0814 11.4291 14.1269 11.3191C14.1725 11.2091 14.1959 11.0912 14.1959 10.9721V7.19276C14.1956 6.9524 14.0999 6.72199 13.93 6.55204C13.76 6.38208 13.5296 6.28644 13.2893 6.28609V6.28542Z"
                      fill="#F8F0AC"
                    />
                  </svg>
                  Android
                </span>
              </div>
            </div>
          </div>

          <div className={s.sectionDrop_video}>
            <video
              preload={'auto'}
              width={'100%'}
              height={'100%'}
              controls
              muted
              playsInline
              autoPlay
              loop
              src={`${CDN_URL_VIDEOS}/AlphaIntrov7.mp4`}
            ></video>
          </div>
        </div>
      </div>
      <div className="section-03" id="section-3" ref={secondSectionRef}>
        <div className={s.content}>
          <div className={cs(s.left, s.left__02)}>
            <img alt="be-01" src={`${CDN_URL_IMAGES}/02_a.png`}></img>
          </div>
          <div className={s.right}>
            <p className={s.ttitle}>
              Chat with your friends, browse cool content and more.
            </p>
            <div className={s.desc}>
              <p>
                Stay connected and engaged with updates from your network for a
                personalized social experience.
              </p>
            </div>

            <div className={`${s.app_install} ${s.no_bg}`}>
              <div className={s.wInstall}>
                <a
                  href={`${APP_URL}${referral ? `?referral=${referral}` : ''}`}
                  onClick={() => gaEventTracker(AlphaActions.InstallAlphaApp)}
                  className={s.install}
                >
                  <MobilePhoneIcon />
                  <p className={s.title}>Install the app</p>
                </a>
                <p className={s.text}>
                  Available on both <IOSIcon /> <b>iOS</b> and <AndroidIcon />{' '}
                  <b>Android</b>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="section-04" id="section-4" ref={thirdSectionRef}>
        <div className={s.content}>
          <div className={s.left}>
            <img alt="air-01" src={`${CDN_URL_IMAGES}/03_a.png`}></img>
          </div>
          <div className={s.right}>
            <p className={s.ttitle}>Play games.</p>
            <div className={s.desc}>
              <p>
                Have a blast with your community and earn rewards along the way.
              </p>
            </div>

            <div className={`${s.app_install} ${s.no_bg}`}>
              <div className={s.wInstall}>
                <a
                  href={`${APP_URL}${referral ? `?referral=${referral}` : ''}`}
                  onClick={() => gaEventTracker(AlphaActions.InstallAlphaApp)}
                  className={s.install}
                >
                  <MobilePhoneIcon />
                  <p className={s.title}>Install the app</p>
                </a>
                <p className={s.text}>
                  Available on both <IOSIcon /> <b>iOS</b> and <AndroidIcon />{' '}
                  <b>Android</b>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="section-02" id="section-2" ref={fourthSectionRef}>
        <div className={s.content}>
          <div className={cs(s.left)}>
            <img alt="mine-01" src={`${CDN_URL_IMAGES}/04_a.png`}></img>
          </div>
          <div className={s.right}>
            <p className={s.ttitle}>Trade.</p>
            <div className={s.desc}>
              <p>
                Buy and sell access keys, tokens, and coins. Multiple chains are
                supported.
              </p>
            </div>

            <div className={`${s.app_install} ${s.no_bg}`}>
              <div className={s.wInstall}>
                <a
                  href={`${APP_URL}${referral ? `?referral=${referral}` : ''}`}
                  onClick={() => gaEventTracker(AlphaActions.InstallAlphaApp)}
                  className={s.install}
                >
                  <MobilePhoneIcon />
                  <p className={s.title}>Install the app</p>
                </a>
                <p className={s.text}>
                  Available on both <IOSIcon /> <b>iOS</b> and <AndroidIcon />{' '}
                  <b>Android</b>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SectionControllers>
        <div
          onClick={() =>
            firstSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
          className={`dots ${isInViewport1 ? 'active' : ''}`}
        />
        <div
          onClick={() =>
            secondSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
          className={`dots ${isInViewport2 ? 'active' : ''}`}
        />
        <div
          onClick={() =>
            thirdSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
          className={`dots ${isInViewport3 ? 'active' : ''}`}
        />
        <div
          onClick={() =>
            fourthSectionRef.current?.scrollIntoView({ behavior: 'smooth' })
          }
          className={`dots ${isInViewport4 ? 'active' : ''}`}
        />
      </SectionControllers>
    </StyledAbout>
  );
};

export default AlphaWtfPage;
